import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Empty, Popconfirm, Select, Space, Switch, Tag, Typography } from "antd";

import type { AgentChatSession, AgentSkill } from "@fitness-app/data-models";
import type { TeamMemberEntity } from "@fitness-app/data-models/entities/TeamMember";

import { formatSessionDate } from "./constants";
import { useTranslation as useInterfaceTranslation } from "react-i18next";

type SalesAgentChatOwner = Pick<TeamMemberEntity, "id" | "firstName" | "lastName" | "email" | "role" | "userId">;

type SalesAgentPanelProps = {
  activeSessionId: string | null;
  canInspectSalesRepChats: boolean;
  canManageSkills: boolean;
  deleteSessionLoading: boolean;
  deleteSkillLoading: boolean;
  enabledSkillsCount: number;
  historyOwnerId: string | null | undefined;
  isChatBusy: boolean;
  isViewingOwnChats: boolean;
  onDeleteSession: (sessionId: string) => void;
  onDeleteSkill: (skill: AgentSkill) => void;
  onEditSkill: (skill: AgentSkill) => void;
  onNewChat: () => void;
  onNewSkill: () => void;
  onSelectHistoryOwner: (ownerId: string) => void;
  onSelectSession: (sessionId: string) => void;
  onToggleSkill: (skill: AgentSkill, enabled: boolean) => void;
  sellers: SalesAgentChatOwner[];
  sellersLoading: boolean;
  sessions: AgentChatSession[];
  sessionsError: Error | null;
  skills: AgentSkill[];
  skillsError: Error | null;
  toggleSkillLoading: boolean;
  userId: string | null | undefined;
};

const SalesAgentHistory = ({
  activeSessionId,
  canInspectSalesRepChats,
  deleteSessionLoading,
  historyOwnerId,
  isChatBusy,
  isViewingOwnChats,
  onDeleteSession,
  onNewChat,
  onSelectHistoryOwner,
  onSelectSession,
  sellers,
  sellersLoading,
  sessions,
  sessionsError,
  userId,
}: Pick<
  SalesAgentPanelProps,
  | "activeSessionId"
  | "canInspectSalesRepChats"
  | "deleteSessionLoading"
  | "historyOwnerId"
  | "isChatBusy"
  | "isViewingOwnChats"
  | "onDeleteSession"
  | "onNewChat"
  | "onSelectHistoryOwner"
  | "onSelectSession"
  | "sellers"
  | "sellersLoading"
  | "sessions"
  | "sessionsError"
  | "userId"
>) => {
  const { t: tUi } = useInterfaceTranslation();
  return (
    <section className="flex min-h-0 flex-[0.95] flex-col border-b border-gray-100 px-4 py-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Historia
          </Typography.Text>
          <Typography.Title level={5} className="!mb-0 !mt-1">
            Czat sprzedażowy
          </Typography.Title>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={onNewChat} disabled={isChatBusy || !userId}>
          Nowy
        </Button>
      </div>
      {canInspectSalesRepChats ? (
        <Select
          className="mb-3 w-full"
          loading={sellersLoading}
          value={historyOwnerId || undefined}
          onChange={onSelectHistoryOwner}
          options={[
            { label: "Moje czaty", value: userId },
            ...sellers.map((seller) => ({
              label: `${seller.firstName} ${seller.lastName}`,
              value: seller.userId ?? "",
            })),
          ].filter((option) => Boolean(option.value))}
        />
      ) : null}
      {sessionsError ? (
        <Alert type="warning" showIcon message="Nie udało się pobrać historii" description={sessionsError.message} />
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {sessions.length ? (
          <div className="space-y-2">
            {sessions.map((session) => (
              <div
                key={session.id}
                className={[
                  "group flex items-start gap-2 rounded-md border px-3 py-2 transition-colors",
                  session.id === activeSessionId
                    ? "border-[#a28f70] bg-[#f8f4ee]"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
                ].join(" ")}
              >
                <button
                  type="button"
                  disabled={isChatBusy}
                  onClick={() => onSelectSession(session.id)}
                  className="min-w-0 flex-1 text-left disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div className="truncate text-sm font-semibold text-gray-900">{session.title}</div>
                  <div className="mt-1 text-xs text-gray-500">{formatSessionDate(tUi, session.lastMessageAt)}</div>
                </button>
                {isViewingOwnChats ? (
                  <Popconfirm
                    title="Usunąć rozmowę?"
                    description="Usunie sesję i wszystkie wiadomości."
                    okText="Usuń"
                    cancelText="Anuluj"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => onDeleteSession(session.id)}
                    disabled={isChatBusy || deleteSessionLoading}
                  >
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={isChatBusy || deleteSessionLoading}
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </Popconfirm>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak zapisanych rozmów" />
        )}
      </div>
    </section>
  );
};

const SalesAgentSkills = ({
  canManageSkills,
  deleteSkillLoading,
  enabledSkillsCount,
  onDeleteSkill,
  onEditSkill,
  onNewSkill,
  onToggleSkill,
  skills,
  skillsError,
  toggleSkillLoading,
}: Pick<
  SalesAgentPanelProps,
  | "deleteSkillLoading"
  | "canManageSkills"
  | "enabledSkillsCount"
  | "onDeleteSkill"
  | "onEditSkill"
  | "onNewSkill"
  | "onToggleSkill"
  | "skills"
  | "skillsError"
  | "toggleSkillLoading"
>) => (
  <section className="flex min-h-0 flex-1 flex-col px-4 py-4">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div>
        <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Ustawienia
        </Typography.Text>
        <Typography.Title level={5} className="!mb-0 !mt-1">
          Skille
        </Typography.Title>
      </div>
      <Space>
        <Tag color="green">{enabledSkillsCount}</Tag>
        {canManageSkills ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={onNewSkill}>
            Dodaj
          </Button>
        ) : null}
      </Space>
    </div>
    {skillsError ? (
      <Alert
        type="warning"
        showIcon
        message="Nie udało się pobrać skillów"
        description={skillsError.message}
        className="mb-3"
      />
    ) : null}
    <div className="min-h-0 flex-1 overflow-y-auto pr-1">
      {skills.map((skill) => (
        <div key={skill.id} className="border-b border-gray-100 py-3">
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Typography.Text strong className="block truncate">
                {skill.name}
              </Typography.Text>
              <Typography.Text type="secondary" className="text-xs">
                {skill.description || skill.slug}
              </Typography.Text>
            </div>
            <Switch
              size="small"
              checked={skill.enabled}
              loading={toggleSkillLoading}
              disabled={!canManageSkills}
              onChange={(enabled) => onToggleSkill(skill, enabled)}
            />
          </div>
          {canManageSkills ? (
            <Space>
              <Button size="small" icon={<EditOutlined />} onClick={() => onEditSkill(skill)}>
                Edytuj
              </Button>
              <Popconfirm
                title="Usunąć skill?"
                okText="Usuń"
                cancelText="Anuluj"
                onConfirm={() => onDeleteSkill(skill)}
                disabled={deleteSkillLoading}
              >
                <Button size="small" danger icon={<DeleteOutlined />} disabled={deleteSkillLoading}>
                  Usuń
                </Button>
              </Popconfirm>
            </Space>
          ) : null}
        </div>
      ))}
    </div>
  </section>
);

export const SalesAgentPanel = (props: SalesAgentPanelProps) => (
  <div className="flex h-full min-h-0 flex-col">
    <div className="border-b border-gray-100 px-5 py-4">
      <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Sales agent
      </Typography.Text>
      <Typography.Title level={4} className="!mb-0 !mt-1">
        Panel agenta
      </Typography.Title>
    </div>
    <SalesAgentHistory {...props} />
    <SalesAgentSkills {...props} />
  </div>
);
